<template>
  <!--
    用 Element 的 el-dialog 承载（原来是手写的 .request-mask + .request-dialog）。
    ⚠️ :modal-append-to-body="false" 是必须的，原因见 ShareSettingDialog.vue 顶部注释：
    默认 true 时遮罩会被挂到 body，反而盖住留在 .frame 里的对话框。

    垂直居中由 App.vue 的全局 `.el-dialog__wrapper{display:flex;align-items:center}` 负责，
    这里**不要再写 top="8vh"**（它只是往下推 margin-top，做不到居中，还会和全局规则打架）。
  -->
  <el-dialog
    :visible="visible"
    title="好友申请"
    width="640px"
    :modal-append-to-body="false"
    @close="close"
  >
    <div class="tabs">
      <button
        class="tab"
        :class="{ active: activeTab === 'INCOMING' }"
        type="button"
        @click="activeTab = 'INCOMING'"
      >收到的 {{ incoming.length ? `(${incoming.length})` : '' }}</button>
      <button
        class="tab"
        :class="{ active: activeTab === 'OUTGOING' }"
        type="button"
        @click="activeTab = 'OUTGOING'"
      >发出的 {{ outgoing.length ? `(${outgoing.length})` : '' }}</button>
    </div>

    <div class="request-list">
      <div v-if="loading" class="tip">加载中…</div>
      <div v-else-if="!currentList.length" class="tip">暂无申请</div>
      <div v-for="r in currentList" :key="r.id" class="request-item">
        <UserAvatar class="request-avatar" :url="r.avatarUrl" :name="r.nickname" />
        <div class="request-info">
          <div class="request-name">{{ r.nickname }}</div>
          <div class="request-time">{{ formatTime(r.createdAt) }}</div>
        </div>
        <div class="request-actions">
          <template v-if="activeTab === 'INCOMING'">
            <button class="round-btn accept" type="button" title="接受" @click="handleAccept(r)">
              <FaIcon :icon="faCheck" :size="17" />
            </button>
            <button class="round-btn reject" type="button" title="拒绝" @click="handleReject(r)">
              <FaIcon :icon="faXmark" :size="17" />
            </button>
          </template>
          <button v-else class="round-btn undo" type="button" title="撤回申请" @click="handleCancel(r)">
            <FaIcon :icon="faRotateLeft" :size="17" />
          </button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import FaIcon from '@/components/common/FaIcon.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck'
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons/faRotateLeft'
import {
  listFriendRequests,
  acceptFriendRequest,
  rejectFriendRequest,
  removeFriend
} from '@/api/friend.js'
import { runAchievementCheck } from '@/components/achievement/achievementStore.js'

export default {
  name: 'FriendRequestPanel',
  components: { FaIcon, UserAvatar },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeTab: 'INCOMING',
      incoming: [],
      outgoing: [],
      loading: false,
      faXmark,
      faCheck,
      faRotateLeft
    }
  },
  computed: {
    currentList() {
      return this.activeTab === 'INCOMING' ? this.incoming : this.outgoing
    }
  },
  watch: {
    visible(val) {
      if (val) this.load()
    }
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const [inRes, outRes] = await Promise.all([
          listFriendRequests('INCOMING'),
          listFriendRequests('OUTGOING')
        ])
        this.incoming = (inRes.success && inRes.data) ? inRes.data : []
        this.outgoing = (outRes.success && outRes.data) ? outRes.data : []
      } catch (e) {
        console.error('[FriendRequestPanel] 加载申请失败', e)
      } finally {
        this.loading = false
      }
    },
    async handleAccept(request) {
      try {
        const res = await acceptFriendRequest(request.userId, {})
        if (!res.success || res.code !== 200) {
          alert(res.message || '操作失败')
          return
        }
        await this.load()
        this.$emit('changed')
        // 通过好友申请可能达成「初次相遇 / 好友数量」类成就
        runAchievementCheck(true)
      } catch (e) {
        alert('网络异常，请稍后重试')
      }
    },
    async handleReject(request) {
      try {
        const res = await rejectFriendRequest(request.userId)
        if (!res.success || res.code !== 200) {
          alert(res.message || '操作失败')
          return
        }
        await this.load()
        this.$emit('changed')
      } catch (e) {
        alert('网络异常，请稍后重试')
      }
    },
    async handleCancel(request) {
      try {
        const res = await removeFriend(request.userId)
        if (!res.success || res.code !== 200) {
          alert(res.message || '操作失败')
          return
        }
        await this.load()
        this.$emit('changed')
      } catch (e) {
        alert('网络异常，请稍后重试')
      }
    },
    formatTime(value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d.getTime())) return ''
      return `${d.getMonth() + 1}/${d.getDate()}`
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* 弹窗外壳（遮罩 / 白底 / 描边 / 圆角）现在由 App.vue 里的全局 .el-dialog 主题负责，
   这里只保留弹窗内部内容的样式。
   尺寸整体比改造前放大了一档（用户要求「悬浮窗设计得大一些」）：
   宽度 400 → 640px，头像 32 → 40px，操作按钮 30 → 36px / 图标 12 → 17px，
   列表区高度上限 420px。
   2026-10-03 再放大一档字号（用户要求「文字更舒适」）：标签 / 空态 14 → 15px，
   好友名 15 → 16px，时间等次要说明 12 → 13px，头像首字母 16 → 17px，并统一行高。 */
.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.tab {
  border: 2px solid #91a67b;
  background: #f7f7df;
  color: #57795c;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  padding: 8px 18px;
  border-radius: 14px;
  cursor: pointer;
}

.tab.active {
  background: #f1dc7b;
  border-color: #987a2c;
  color: #263b29;
}

.request-list {
  max-height: min(50vh, 420px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 2px;
}

.tip {
  color: #8a9a78;
  font-size: 15px;
  line-height: 1.6;
  text-align: center;
  padding: 24px 0;
}

.request-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.request-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #a8c8e0;
  border: 2px solid #57795c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.request-info {
  flex: 1;
  min-width: 0;
}

.request-name {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.45;
  color: #263b29;
}

.request-time {
  font-size: 13px;
  line-height: 1.5;
  color: #8a9a78;
  margin-top: 2px;
}

.request-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.round-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.round-btn.accept {
  background: #e7f2d4;
  color: #3f7a3f;
}

.round-btn.accept:hover {
  background: #97c459;
  color: #fff;
}

.round-btn.reject {
  background: #fde8e8;
  color: #c45c5c;
}

.round-btn.reject:hover {
  background: #c45c5c;
  color: #fff;
}

.round-btn.undo {
  background: #eef2e2;
  color: #57795c;
}

.round-btn.undo:hover {
  background: #d5e2b4;
  color: #35512d;
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸沿用上面的值。 */
/* 收到的 / 发出的 切换标签 */
.tab{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.875rem;
  color:var(--lp-text-2,#5f7268);
}
.tab.active{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
  color:var(--lp-primary-dark,#2f7a4a);
}
.tip{
  color:var(--lp-text-3,#94a59b);
}
/* 申请行 */
.request-avatar{
  background:var(--lp-primary,#4aa46a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
.request-name{
  color:var(--lp-text,#26332b);
}
.request-time{
  color:var(--lp-text-3,#94a59b);
}
/* 圆形操作按钮（保留圆形，只改色） */
.round-btn.accept{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.round-btn.accept:hover{
  background:var(--lp-primary,#4aa46a);
  color:#fff;
}
.round-btn.reject{
  background:#fdecec;
  color:#d9534f;
}
.round-btn.reject:hover{
  background:#d9534f;
  color:#fff;
}
.round-btn.undo{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.round-btn.undo:hover{
  background:var(--lp-primary-soft2,#d5ebdb);
  color:var(--lp-primary-dark,#2f7a4a);
}
</style>
